import { CampaignFilterItem, CampaignFilterName } from "../types/campaign-filter";

import { LS_CAMPAIGN_DASHBOARD } from "@/entities/localstorage/model/const/localstorage_names";
import { getAuthSessionId } from "@/entities/localstorage/model/lib/authSessionStorage";
import { parseDateSelection, SessionDateSelection } from "@/shared/lib/dateFilter/sessionDateRange";
import { FilterType } from "@/shared/model/types/filter-type";

const STORAGE_VERSION = 1;

const filterNames = new Set<CampaignFilterName>([
    "campaign_id",
    "clicks_count",
    "spend",
    "installs_count",
    "impressions_count",
    "app",
    "platform",
    "status",
]);

const filterTypes = new Set<FilterType>([
    "input",
    "range",
    "range-number",
    "select",
    "multi-select",
    "textarea",
]);

export type StoredCampaignFilter = Pick<CampaignFilterItem, "name" | "type" | "value" | "value_to">;

export interface CampaignDashboardState {
    filters: StoredCampaignFilter[];
    dateSelection: SessionDateSelection;
}

interface CampaignDashboardSnapshot extends CampaignDashboardState {
    version: typeof STORAGE_VERSION;
    sessionId: string;
}

const parseFilter = (value: unknown): StoredCampaignFilter | null => {
    if (!value || typeof value !== "object") return null;

    const filter = value as Partial<CampaignFilterItem>;
    if (
        !filterNames.has(filter.name as CampaignFilterName) ||
        !filterTypes.has(filter.type as FilterType) ||
        typeof filter.value !== "string"
    ) {
        return null;
    }

    if (filter.value_to !== undefined && typeof filter.value_to !== "string") return null;

    return {
        name: filter.name as CampaignFilterName,
        type: filter.type as FilterType,
        value: filter.value,
        ...(filter.value_to !== undefined ? { value_to: filter.value_to } : {}),
    };
};

const serializeFilters = (filters: CampaignFilterItem[]): StoredCampaignFilter[] =>
    filters.map(({ name, type, value, value_to }) => ({
        name,
        type,
        value,
        ...(value_to !== undefined ? { value_to } : {}),
    }));

export const getCampaignDashboardState = (): CampaignDashboardState | null => {
    const sessionId = getAuthSessionId();
    if (!sessionId) return null;

    try {
        const value: unknown = JSON.parse(localStorage.getItem(LS_CAMPAIGN_DASHBOARD) || "null");
        if (!value || typeof value !== "object") return null;

        const snapshot = value as Partial<CampaignDashboardSnapshot>;
        if (snapshot.version !== STORAGE_VERSION || snapshot.sessionId !== sessionId) return null;
        if (!Array.isArray(snapshot.filters)) return null;

        const filters = snapshot.filters.map(parseFilter);
        if (filters.some((filter) => filter === null)) return null;

        const dateSelection = parseDateSelection(snapshot.dateSelection);
        if (!dateSelection) return null;

        return {
            filters: filters as StoredCampaignFilter[],
            dateSelection,
        };
    } catch {
        localStorage.removeItem(LS_CAMPAIGN_DASHBOARD);
        return null;
    }
};

export const saveCampaignDashboardState = ({
    filters,
    dateSelection,
}: {
    filters: CampaignFilterItem[];
    dateSelection: SessionDateSelection;
}) => {
    const sessionId = getAuthSessionId();
    if (!sessionId) return;

    const snapshot: CampaignDashboardSnapshot = {
        version: STORAGE_VERSION,
        sessionId,
        filters: serializeFilters(filters),
        dateSelection,
    };

    localStorage.setItem(LS_CAMPAIGN_DASHBOARD, JSON.stringify(snapshot));
};

export const clearCampaignDashboardState = () => {
    localStorage.removeItem(LS_CAMPAIGN_DASHBOARD);
};
